import { gql } from '@apollo/client';

// ==================== Fragments ====================

export const ACTIVITY_FRAGMENT = gql`
  fragment ActivityFields on Activity {
    id
    organizationId
    contactId
    type
    description
    activityDate
    createdBy
    metadata {
      callDirection
      callOutcome
      subject
      recipients
      body
      meetingTitle
      startTime
      endTime
      location
      attendees
    }
    contact {
      id
      firstName
      lastName
      email
      phone
      type
    }
    createdAt
    updatedAt
  }
`;

// ==================== Queries ====================

export const GET_ACTIVITIES = gql`
  query GetActivities(
    $organizationId: ID!
    $type: ActivityType
    $limit: Int
    $offset: Int
  ) {
    activities(
      organizationId: $organizationId
      type: $type
      limit: $limit
      offset: $offset
    ) {
      items {
        ...ActivityFields
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        totalCount
        totalPages
        currentPage
      }
    }
  }
  ${ACTIVITY_FRAGMENT}
`;

export const GET_ACTIVITY = gql`
  query GetActivity($id: ID!) {
    activity(id: $id) {
      ...ActivityFields
    }
  }
  ${ACTIVITY_FRAGMENT}
`;

// ==================== Mutations ====================

export const CREATE_ACTIVITY = gql`
  mutation CreateActivity($input: CreateActivityInput!) {
    createActivity(input: $input) {
      ...ActivityFields
    }
  }
  ${ACTIVITY_FRAGMENT}
`;

export const UPDATE_ACTIVITY = gql`
  mutation UpdateActivity($id: ID!, $input: UpdateActivityInput!) {
    updateActivity(id: $id, input: $input) {
      ...ActivityFields
    }
  }
  ${ACTIVITY_FRAGMENT}
`;

export const DELETE_ACTIVITY = gql`
  mutation DeleteActivity($id: ID!) {
    deleteActivity(id: $id)
  }
`;

// ==================== TypeScript Types ====================

export type ActivityType = 'CALL' | 'EMAIL' | 'MEETING' | 'NOTE';

export interface ActivityMetadata {
  callDirection?: string;
  callOutcome?: string;
  subject?: string;
  recipients?: string[];
  body?: string;
  meetingTitle?: string;
  startTime?: string;
  endTime?: string;
  location?: string;
  attendees?: string[];
}

export interface ActivityContact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  type: string;
}

export interface Activity {
  id: string;
  organizationId: string;
  contactId?: string;
  type: ActivityType;
  description: string;
  activityDate: string;
  createdBy: string;
  metadata?: ActivityMetadata;
  contact?: ActivityContact;
  createdAt: string;
  updatedAt: string;
}

export interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalCount: number;
  totalPages: number;
  currentPage: number;
}

export interface ActivityConnection {
  items: Activity[];
  pageInfo: PageInfo;
}

// Query variable types
export interface GetActivitiesVariables {
  organizationId: string;
  type?: ActivityType;
  limit?: number;
  offset?: number;
}

export interface GetActivityVariables {
  id: string;
}

// Mutation input types
export interface CreateActivityInput {
  organizationId: string;
  contactId?: string;
  type: ActivityType;
  description: string;
  activityDate?: string;
  metadata?: ActivityMetadata;
}

export interface UpdateActivityInput {
  contactId?: string;
  type?: ActivityType;
  description?: string;
  activityDate?: string;
  metadata?: ActivityMetadata;
}

// Query/Mutation response types
export interface GetActivitiesData {
  activities: ActivityConnection;
}

export interface GetActivityData {
  activity: Activity | null;
}

export interface CreateActivityData {
  createActivity: Activity;
}

export interface UpdateActivityData {
  updateActivity: Activity;
}

export interface DeleteActivityData {
  deleteActivity: boolean;
}
